import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Text } from "@mantine/core";
import { IconCircleCheckFilled } from "@tabler/icons-react";

type LoadingState = {
  text: string;
};

interface MultiStepLoaderProps {
  loadingStates: LoadingState[];
  loading?: boolean;
  duration?: number;
  generationKey?: string | number;
  title?: string;
  subtitle?: string;
  inline?: boolean;
  minimal?: boolean;
  textSize?: string;
}

export function MultiStepLoader({ loadingStates, loading, duration = 2000, generationKey, title = "GTsetu AI is generating your profile", subtitle = "Please wait a moment. It may take some time to generate your profile.", inline = false, minimal = false, textSize = "sm" }: MultiStepLoaderProps) {
  const [currentState, setCurrentState] = useState(0);

  // Advance step on interval, looping back to start
  useEffect(() => {
    if (!loading) return;
    const timeout = setTimeout(() => {
      setCurrentState((prev) => (prev < loadingStates.length - 1 ? prev + 1 : prev));
    }, duration);
    return () => clearTimeout(timeout);
  }, [currentState, loading, loadingStates.length, duration]);

  // Reset when a new generation starts
  useEffect(() => {
    setCurrentState(0);
  }, [generationKey]);

  return (
    <AnimatePresence mode="wait">
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: inline ? "absolute" : "fixed",
            inset: 0,
            zIndex: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.75)",
            backdropFilter: "blur(6px)",
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={minimal ? { textAlign: "center" } : {
              background: "#f0f4fc",
              borderRadius: 22,
              padding: "56px 56px",
              minWidth: "42vw",
              maxWidth: "52vw",
              minHeight: "52vh",
              border: "1px solid #E5E7EB",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!minimal && (
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
                <img src="/gtLogo.svg" alt="GTsetu" style={{ height: 150, width: "auto" }} />
              </div>
            )}

            {title && (
              <Text fz={34} fw={600} mb={12} c="slate.10">
                {title}
              </Text>
            )}
            {subtitle && (
              <Text fz={20} c="slate.7" mb={28}>
                {subtitle}
              </Text>
            )}
            <Text fz={14} c="slate.6" mb={20}>
              Do not refresh or close this page
            </Text>

            <div style={{ width: "100%", maxWidth: 740 }}>
              {loadingStates.map((state, index) => {
                const isCompleted = index < currentState;
                const isCurrent = index === currentState;
                const isVisible = index <= currentState;

                if (!isVisible) return null;

                return (
                  <motion.div
                    key={`${generationKey ?? "gen"}-${index}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                      marginBottom: 10,
                    }}
                  >
                    <IconCircleCheckFilled
                      size={22}
                      color={isCompleted ? "#16A34A" : isCurrent ? "#94A3B8" : "#CBD5E1"}
                    />
                    <Text fz={textSize === "sm" ? 22 : textSize} fw={isCurrent ? 600 : 500} c={isCurrent ? "slate.9" : "slate.7"}>
                      {state.text}
                    </Text>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
